import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { type TaskBasis } from "@/store";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TaskBasisPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskBasis: TaskBasis | null;
  companyName: string;
  columnName: string;
}

export function TaskBasisPanel({
  open,
  onOpenChange,
  taskBasis,
  companyName,
  columnName,
}: TaskBasisPanelProps) {
  if (!taskBasis) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Research Basis</SheetTitle>
          <SheetDescription>
            {companyName} - {columnName}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-120px)] mt-6">
          <div className="space-y-4 pr-4">
            {taskBasis.basis && taskBasis.basis.length > 0 ? (
              taskBasis.basis.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="space-y-2">
                    {typeof item === "string" ? (
                      <p className="text-sm text-gray-700">{item}</p>
                    ) : typeof item === "object" && item !== null ? (
                      <>
                        {item.title && (
                          <h4 className="font-medium text-sm text-gray-900">
                            {item.title}
                          </h4>
                        )}
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline block"
                          >
                            {item.url}
                          </a>
                        )}
                        {item.content && (
                          <p className="text-sm text-gray-700">{item.content}</p>
                        )}
                        {item.snippet && (
                          <p className="text-sm text-gray-700">{item.snippet}</p>
                        )}
                        {/* Display any other properties */}
                        {Object.entries(item).map(([key, value]) => {
                          if (
                            key !== "title" &&
                            key !== "url" &&
                            key !== "content" &&
                            key !== "snippet" &&
                            typeof value === "string"
                          ) {
                            return (
                              <div key={key} className="text-sm">
                                <span className="font-medium text-gray-700">
                                  {key}:{" "}
                                </span>
                                <span className="text-gray-600">{value}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </>
                    ) : (
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(item, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No research basis available.</p>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
